// src/app/services/notification-filter.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NotificationItem } from '../../models/notification.model';

export enum FilterType {
  All = 'all',
  Unread = 'unread', 
  Read = 'read'
}

export interface FilterOption {
  value: FilterType;
  labelKey: string;
  count?: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationFilterService {
  private currentFilterSubject = new BehaviorSubject<FilterType>(FilterType.All);
  public currentFilter$ = this.currentFilterSubject.asObservable();

  private allNotificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  public allNotifications$ = this.allNotificationsSubject.asObservable();

  private filteredNotificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  public filteredNotifications$ = this.filteredNotificationsSubject.asObservable();

  getCurrentFilter(): FilterType {
    return this.currentFilterSubject.value;
  }

  getCurrentIsReadFilter(): boolean | undefined {
    switch (this.currentFilterSubject.value) {
      case FilterType.Read:
        return true;
      case FilterType.Unread:
        return false;
      default:
        return undefined;
    }
  }

  setFilter(filter: FilterType): void {
    console.log('[NotificationFilterService] Setting filter to:', filter);
    this.currentFilterSubject.next(filter);
    this.applyFilter();
  }

  setNotifications(notifications: NotificationItem[]): void {
    console.log('[NotificationFilterService] Setting notifications:', notifications.length);
    const sortedNotifications = this.sortByNewest(notifications);
    this.allNotificationsSubject.next(sortedNotifications);
    this.applyFilter();
  }

  private applyFilter(): void {
    const allNotifications = this.allNotificationsSubject.value;
    const currentFilter = this.currentFilterSubject.value;
    
    let filtered: NotificationItem[];

    switch (currentFilter) {
      case FilterType.Unread:
        filtered = allNotifications.filter(n => !n.isRead);
        break;
      case FilterType.Read:
        filtered = allNotifications.filter(n => n.isRead);
        break;
      case FilterType.All:
      default:
        filtered = allNotifications;
        break;
    }

    console.log(`[NotificationFilterService] Applied filter '${currentFilter}': ${filtered.length} results`);
    this.filteredNotificationsSubject.next(filtered);
  }

  private sortByNewest(notifications: NotificationItem[]): NotificationItem[] {
    return [...notifications].sort((a, b) => 
      new Date(b.created).getTime() - new Date(a.created).getTime()
    );
  }

  getFilterOptions(notifications: NotificationItem[]): FilterOption[] {
    const unreadCount = notifications.filter(n => !n.isRead).length;
    const readCount = notifications.filter(n => n.isRead).length;
    const totalCount = notifications.length;

    return [
      {
        value: FilterType.All,
        labelKey: 'FILTER.ALL',
        count: totalCount
      },
      {
        value: FilterType.Unread,
        labelKey: 'FILTER.UNREAD',
        count: unreadCount
      },
      {
        value: FilterType.Read,
        labelKey: 'FILTER.READ',
        count: readCount
      }
    ];
  }

  reset(): void {
    this.setFilter(FilterType.All);
    this.allNotificationsSubject.next([]);
    this.filteredNotificationsSubject.next([]);
  }
}
